import { Link } from "react-router-dom";
import { blogPosts, BlogPost } from "@/data/blogPosts";
import { ArrowRight } from "lucide-react";

interface RelatedPostsProps {
  currentSlug: string;
  maxPosts?: number;
}

export const RelatedPosts = ({ currentSlug, maxPosts = 3 }: RelatedPostsProps) => {
  // Get related posts by finding posts with similar keywords in title/content
  const currentPost = blogPosts.find(p => p.slug === currentSlug);
  
  if (!currentPost) return null;

  // Simple keyword matching for relatedness
  const getKeywords = (post: BlogPost): string[] => {
    const text = `${post.title} ${post.excerpt}`.toLowerCase();
    const keywords: string[] = [];
    
    if (text.includes("lawn care") || text.includes("lawn mowing")) keywords.push("lawn");
    if (text.includes("landscaping") || text.includes("landscaper")) keywords.push("landscaping");
    if (text.includes("seo") || text.includes("ranking")) keywords.push("seo");
    if (text.includes("template") || text.includes("checklist")) keywords.push("template");
    if (text.includes("design") || text.includes("website")) keywords.push("design");
    if (text.includes("quote") || text.includes("form")) keywords.push("form");
    if (text.includes("local") || text.includes("service area")) keywords.push("local");
    
    return keywords;
  };

  const currentKeywords = getKeywords(currentPost);

  const relatedPosts = blogPosts
    .filter(post => post.slug !== currentSlug)
    .map(post => {
      const postKeywords = getKeywords(post);
      const matchCount = currentKeywords.filter(kw => postKeywords.includes(kw)).length;
      return { post, matchCount };
    })
    .sort((a, b) => b.matchCount - a.matchCount)
    .slice(0, maxPosts)
    .map(item => item.post);

  if (relatedPosts.length === 0) return null;

  return (
    <section className="mt-12 pt-8 border-t">
      <h2 className="text-xl md:text-2xl font-bold mb-6">Related Articles</h2>
      <div className="grid gap-4">
        {relatedPosts.map(post => (
          <Link 
            key={post.slug}
            to={`/blog/${post.slug}`}
            className="group flex items-start gap-4 p-4 rounded-lg border bg-card hover:border-primary transition-colors"
          >
            <div className="flex-1">
              <h3 className="font-semibold group-hover:text-primary transition-colors line-clamp-2">
                {post.title}
              </h3>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                {post.excerpt}
              </p>
            </div>
            <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0 mt-1" />
          </Link>
        ))}
      </div>
    </section>
  );
};
